import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ShieldBan, AlertTriangle, User, Globe, Eye } from 'lucide-react'
import { apiRequest } from '../api/client'

interface UserHit {
  id: string
  email: string | null
  name: string | null
}

interface AccessBlock {
  id: string
  target_type: 'user' | 'ip' | 'visitor'
  target_value: string
  matched_email?: string | null
  severity: 'soft' | 'hard'
  reason: string | null
  created_by: string
  created_at: string
  expires_at: string | null
  lifted_at: string | null
  lifted_by: string | null
  lifted_note: string | null
  active: boolean
}

const TARGETS = [
  { value: 'user', label: 'User account', icon: User,
    placeholder: 'someone@example.com',
    help: "Their email address — the same one they sign in with. The block is stored against their account, so it follows them across devices and networks, and survives an email change." },
  { value: 'ip', label: 'IP address or range', icon: Globe,
    placeholder: '203.0.113.9   or   203.0.113.0/24',
    help: 'A single address, or a CIDR range to cover a whole network. Catches abuse from people who are not signed in. Find the address in your Cloudflare logs.' },
  { value: 'visitor', label: 'Help Bot visitor', icon: Eye,
    placeholder: '00000000-0000-0000-0000-000000000000',
    help: 'The visitor id shown on the Help Bot Sessions page. Use this instead of an IP when an office shares one address and you only want to stop one browser.' },
] as const

function targetIcon(t: AccessBlock['target_type']) {
  const found = TARGETS.find((x) => x.value === t)
  return found ? found.icon : Globe
}

export default function AccessBlocks() {
  const queryClient = useQueryClient()
  const [includeLifted, setIncludeLifted] = useState(false)
  const [targetType, setTargetType] = useState<'user' | 'ip' | 'visitor'>('user')
  const [targetValue, setTargetValue] = useState('')
  const [severity, setSeverity] = useState<'soft' | 'hard'>('soft')
  const [reason, setReason] = useState('')
  const [warnings, setWarnings] = useState<string[]>([])
  const [matched, setMatched] = useState<string | null>(null)
  // Confirmations are DaisyUI modals, matching EmailOrganizations. A native
  // window.confirm cannot show the target in bold, cannot style the difference
  // between a soft and a hard block, and looks like a browser error rather
  // than a deliberate step in the admin app.
  const [confirmBlock, setConfirmBlock] = useState(false)
  const [confirmLift, setConfirmLift] = useState<AccessBlock | null>(null)
  // Typeahead for user blocks. Nothing in the portal displays a user id, and
  // asking an admin to fetch one from the Users page before they can act is a
  // dead end — so they type a name or an email and pick the account.
  const [pickerOpen, setPickerOpen] = useState(false)
  const [debounced, setDebounced] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setDebounced(targetValue.trim()), 250)
    return () => clearTimeout(t)
  }, [targetValue])

  const { data: matches, isFetching: searching } = useQuery<{ users: UserHit[] }>({
    queryKey: ['access-block-user-search', debounced],
    // Two characters is the floor: one letter matches most of the directory and
    // the list is useless.
    enabled: targetType === 'user' && debounced.length >= 2,
    queryFn: () =>
      apiRequest(`/api/admin/users?limit=8&search=${encodeURIComponent(debounced)}`),
    staleTime: 30000,
  })
  const [error, setError] = useState<string | null>(null)

  const { data, isLoading } = useQuery<{ blocks: AccessBlock[] }>({
    queryKey: ['access-blocks', includeLifted],
    queryFn: () => apiRequest(`/api/admin/access-blocks?include_lifted=${includeLifted}`),
  })

  const create = useMutation({
    mutationFn: () =>
      apiRequest<{ block: AccessBlock; warnings: string[] }>('/api/admin/access-blocks', {
        method: 'POST',
        body: JSON.stringify({
          target_type: targetType,
          target_value: targetValue.trim(),
          severity,
          reason: reason.trim() || null,
        }),
      }),
    onSuccess: (res) => {
      setWarnings(res.warnings || [])
      setError(null)
      setMatched(res.block?.matched_email ? `Blocked ${res.block.matched_email}` : null)
      setConfirmBlock(false)
      setTargetValue('')
      setReason('')
      queryClient.invalidateQueries({ queryKey: ['access-blocks'] })
    },
    onError: (e: Error) => { setError(e.message || 'Could not create the block.'); setWarnings([]); setConfirmBlock(false) },
  })

  const lift = useMutation({
    mutationFn: (id: string) =>
      apiRequest(`/api/admin/access-blocks/${id}/lift`, { method: 'POST', body: JSON.stringify({}) }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['access-blocks'] }); setConfirmLift(null) },
  })

  const onBlock = () => {
    if (!targetValue.trim()) return
    setConfirmBlock(true)
  }

  const blocks = data?.blocks ?? []
  const activeCount = blocks.filter((b) => b.active).length

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="page-title flex items-center gap-2"><ShieldBan className="w-6 h-6" />Blocked access</h1>
        <p className="text-base-content/60 text-sm mt-1">
          Block accounts, IP addresses and Help Bot visitors that are abusing the system.
          Blocks take effect within about 30 seconds.
        </p>
      </div>

      {/* ── new block ─────────────────────────────────────────────── */}
      <div className="card bg-base-100 border border-base-300">
        <div className="card-body gap-4">
          <h2 className="font-semibold">Add a block</h2>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="label"><span className="label-text font-medium">What to block</span></label>
              <select
                className="select select-bordered w-full"
                value={targetType}
                onChange={(e) => setTargetType(e.target.value as typeof targetType)}
              >
                {TARGETS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>

            <div>
              <label className="label"><span className="label-text font-medium">Value</span></label>
              <div className="relative">
                <input
                  className={`input input-bordered w-full text-sm ${targetType === 'user' ? '' : 'font-mono'}`}
                  value={targetValue}
                  onChange={(e) => { setTargetValue(e.target.value); setPickerOpen(true); setMatched(null) }}
                  onFocus={() => setPickerOpen(true)}
                  onBlur={() => setTimeout(() => setPickerOpen(false), 150)}
                  placeholder={TARGETS.find((t) => t.value === targetType)!.placeholder}
                  autoComplete="off"
                  role={targetType === 'user' ? 'combobox' : undefined}
                  aria-expanded={targetType === 'user' ? pickerOpen : undefined}
                  aria-label="Who or what to block"
                />
                {targetType === 'user' && pickerOpen && debounced.length >= 2 && (
                  <ul className="menu menu-sm absolute z-20 mt-1 w-full rounded-box border border-base-300 bg-base-100 shadow-lg max-h-64 overflow-y-auto">
                    {searching && <li className="px-3 py-2 text-base-content/60">Searching…</li>}
                    {!searching && (matches?.users?.length ?? 0) === 0 && (
                      <li className="px-3 py-2 text-base-content/60">
                        No account matches &ldquo;{debounced}&rdquo;
                      </li>
                    )}
                    {matches?.users?.map((u) => (
                      <li key={u.id}>
                        <button
                          type="button"
                          className="flex flex-col items-start gap-0"
                          // onMouseDown, not onClick: the input's onBlur fires
                          // first on click and closes the list before it lands.
                          onMouseDown={(e) => {
                            e.preventDefault()
                            setTargetValue(u.email || u.id)
                            setPickerOpen(false)
                          }}
                        >
                          <span className="font-medium">{u.email || '(no email)'}</span>
                          {u.name && <span className="text-xs text-base-content/60">{u.name}</span>}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <span className="block text-base-content/60 text-xs mt-1">
                {TARGETS.find((t) => t.value === targetType)!.help}
              </span>
            </div>
          </div>

          <div>
            <label className="label"><span className="label-text font-medium">How strict</span></label>
            <div className="flex flex-col gap-2">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="radio" className="radio radio-primary mt-0.5 shrink-0" checked={severity === 'soft'}
                       onChange={() => setSeverity('soft')} />
                <span className="text-sm">
                  <span className="font-medium">Soft — tell them why</span>
                  <span className="block text-base-content/60 text-xs mt-0.5">
                    They can still sign in, but Mat-E refuses to answer and shows them how to
                    contact Member Services to request access back. Use for members.
                  </span>
                </span>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="radio" className="radio radio-primary mt-0.5 shrink-0" checked={severity === 'hard'}
                       onChange={() => setSeverity('hard')} />
                <span className="text-sm">
                  <span className="font-medium">Hard — refuse at the door</span>
                  <span className="block text-base-content/60 text-xs mt-0.5">
                    Rejected before anything loads. There is nowhere in that response to put a
                    reason, so they will not be told why. Use for bots and scrapers.
                  </span>
                </span>
              </label>
            </div>
          </div>

          <div>
            <label className="label">
              <span className="label-text font-medium">Reason</span>
              <span className="label-text-alt text-base-content/50">Optional. Internal note — shown to them only as &ldquo;Reason given&rdquo;.</span>
            </label>
            <input className="input input-bordered w-full" value={reason} onChange={(e) => setReason(e.target.value)}
                   placeholder="e.g. automated scraping, 400+ requests/minute" />
          </div>

          {error && <p className="text-error text-sm">{error}</p>}
          {matched && <p className="text-success text-sm">{matched}</p>}
          {warnings.map((w, i) => (
            <div key={i} className="alert alert-warning text-sm">
              <AlertTriangle className="w-4 h-4 shrink-0" /><span>{w}</span>
            </div>
          ))}

          <div className="flex justify-end">
            <button className="btn btn-error" onClick={onBlock} disabled={create.isPending || !targetValue.trim()}>
              {create.isPending ? 'Blocking…' : 'Block access'}
            </button>
          </div>
        </div>
      </div>

      {/* ── existing blocks ───────────────────────────────────────── */}
      <div className="card bg-base-100 border border-base-300">
        <div className="card-body">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-semibold">{activeCount} active {activeCount === 1 ? 'block' : 'blocks'}</h2>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="checkbox" className="checkbox checkbox-sm" checked={includeLifted}
                     onChange={(e) => setIncludeLifted(e.target.checked)} />
              <span>Show lifted</span>
            </label>
          </div>

          {isLoading ? <p className="text-base-content/60">Loading…</p>
            : blocks.length === 0 ? <p className="text-base-content/60">No blocks. Nobody is being refused access.</p>
            : (
            <div className="overflow-x-auto">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Target</th><th>Strictness</th><th>Reason</th><th>Blocked by</th><th>When</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {blocks.map((b) => {
                    const Icon = targetIcon(b.target_type)
                    return (
                      <tr key={b.id} className={b.active ? '' : 'opacity-50'}>
                        <td>
                          <span className="flex items-center gap-2">
                            <Icon className="w-4 h-4 shrink-0 text-base-content/60" />
                            <span className="font-mono text-xs break-all">{b.target_value}</span>
                          </span>
                          <span className="text-base-content/50 text-xs">{b.target_type}</span>
                        </td>
                        <td>
                          <span className={`badge badge-sm ${b.severity === 'hard' ? 'badge-error' : 'badge-warning'}`}>
                            {b.severity}
                          </span>
                        </td>
                        <td className="max-w-xs"><span className="text-xs">{b.reason || '—'}</span></td>
                        <td className="text-base-content/70 text-xs">{b.created_by}</td>
                        <td className="text-xs whitespace-nowrap">
                          {new Date(b.created_at).toLocaleDateString()}
                          {!b.active && b.lifted_at && (
                            <span className="block text-base-content/50">
                              lifted {new Date(b.lifted_at).toLocaleDateString()}
                              {b.lifted_by ? ` by ${b.lifted_by}` : ''}
                            </span>
                          )}
                        </td>
                        <td>
                          {b.active && (
                            <button className="btn btn-xs btn-ghost" disabled={lift.isPending}
                                    onClick={() => setConfirmLift(b)}>
                              Unblock
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── confirm: block ───────────────────────────────────────── */}
      {confirmBlock && (
        <dialog open className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-2">
              {severity === 'hard' ? 'Refuse this at the door?' : 'Block this access?'}
            </h3>
            <p className="text-base-content/70 mb-2">
              {TARGETS.find((t) => t.value === targetType)!.label}:{' '}
              <strong className="break-all">{targetValue.trim()}</strong>
            </p>
            {severity === 'hard' ? (
              <div className="alert alert-warning text-sm mb-4">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span>
                  They will be refused before anything loads. There is nowhere in that
                  response to put a reason, so they will <strong>not</strong> be told why,
                  and will have no way to ask for access back.
                </span>
              </div>
            ) : (
              <p className="text-base-content/70 mb-4 text-sm">
                They can still sign in, but Mat-E will refuse to answer and will show them
                how to contact Member Services to request access back.
              </p>
            )}
            <div className="flex gap-2 justify-end">
              <button className="btn btn-ghost btn-sm" onClick={() => setConfirmBlock(false)}>Cancel</button>
              <button className="btn btn-error btn-sm" onClick={() => create.mutate()} disabled={create.isPending}>
                {create.isPending ? <span className="loading loading-spinner loading-xs" /> : 'Block access'}
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop"><button onClick={() => setConfirmBlock(false)}>close</button></form>
        </dialog>
      )}

      {/* ── confirm: unblock ─────────────────────────────────────── */}
      {confirmLift && (
        <dialog open className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-2">Restore access?</h3>
            <p className="text-base-content/70 mb-4">
              <strong className="break-all">{confirmLift.target_value}</strong> will be able to
              use Mat-E again. The record of this block is kept.
            </p>
            <div className="flex gap-2 justify-end">
              <button className="btn btn-ghost btn-sm" onClick={() => setConfirmLift(null)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={() => lift.mutate(confirmLift.id)} disabled={lift.isPending}>
                {lift.isPending ? <span className="loading loading-spinner loading-xs" /> : 'Restore access'}
              </button>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop"><button onClick={() => setConfirmLift(null)}>close</button></form>
        </dialog>
      )}
    </div>
  )
}
